import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.jsx'
import { LocaleProvider } from './hooks/useLocale.jsx'
import { AIService } from './services/ai-service.js'
import { ReferralService } from './services/referral-service.js'
import { isNativeApp } from './services/platform-service.js'

// Stash ?ref=VODA-XXXX before routing so it survives the Google sign-in redirect.
ReferralService.capturePendingReferral();
// Android 앱 첫 실행 — Play 가 넘겨준 초대 코드 (가입보다 한참 먼저 끝난다).
ReferralService.captureInstallReferrer();

// Register the PWA service worker only on the web — inside the Capacitor
// native app the SW caching layer is redundant and conflicts with the
// capacitor:// origin.
if (!isNativeApp() && 'serviceWorker' in navigator) {
  // vite-plugin-pwa publishes /registerSW.js which handles auto-update.
  import('virtual:pwa-register').then(({ registerSW }) => {
    registerSW({ immediate: true });
  }).catch(() => {/* PWA disabled or build path missing — ignore */});

  // 옛 'voda-images' runtime cache 일회성 정리 — workbox 의 cleanupOutdatedCaches
  // 는 precache 만 처리해서 runtime cache 는 cacheName bump (v2) 로 우회한 뒤
  // 옛 cache 를 직접 delete. opaque 응답이 cached 채로 남아 EditRegionModal 의
  // CORS 요청과 mismatch 일으키던 버그 대응. caches API 가 idempotent 라
  // 이미 삭제된 cache 면 false 반환, 새 cache 만들면 안 함.
  if ('caches' in window) {
    caches.delete('voda-images').catch(() => {/* ignore */});
  }
}

// Native app: marker class for native-only CSS rules (.header padding-top
// etc.) plus the Capacitor StatusBar plugin in overlay-true mode so the
// webview fills the entire screen and CSS handles the safe-area inset.
if (isNativeApp()) {
  // 앱은 화면이 확대·축소되면 안 된다 — 핀치, 그리고 16px 미만 입력창에 포커스할 때
  // iOS 가 하는 자동 확대까지. 웹은 접근성 때문에 핀치를 남긴다 (더블탭만 CSS 로 끔).
  document.querySelector('meta[name="viewport"]')?.setAttribute(
    'content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover',
  );
  document.documentElement.classList.add('is-native');
  document.body && document.body.classList.add('is-native');
  document.addEventListener('DOMContentLoaded', () => {
    document.body.classList.add('is-native');
  }, { once: true });

  import('@capacitor/status-bar').then(({ StatusBar, Style }) => {
    StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});
    // Style.Light = "dark text for light backgrounds". Our app is white,
    // so we want the system clock / signal / battery glyphs in black.
    // (Style.Dark = light text, used for dark themes — would render the
    // status bar invisible on top of our white chrome.)
    StatusBar.setStyle({ style: Style.Light }).catch(() => {});
  }).catch(() => {/* plugin missing — ignore */});
}

// 아이콘 폰트 로드 후에만 아이콘을 드러낸다. 리거처 폰트라 로드 전에는
// "favorite_border" 같은 원문 글자가 그대로 보이기 때문 (FOIT 회피).
// If the API is missing or the font fails, fall back to revealing after
// 1.5s so icons aren't permanently hidden.
if (document.fonts && document.fonts.load) {
  document.fonts.load('1em "Material Symbols Rounded"').then(() => {
    document.body.classList.add('icons-ready');
  }).catch(() => {
    document.body.classList.add('icons-ready');
  });
  setTimeout(() => document.body.classList.add('icons-ready'), 1500);
} else {
  document.body.classList.add('icons-ready');
}

window.onerror = (message, source, lineno, colno, error) => {
  AIService.logError(error || new Error(String(message)), {
    source, lineno, colno, type: 'uncaught',
  });
};

window.onunhandledrejection = (event) => {
  const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
  AIService.logError(error, { type: 'unhandled_rejection' });
};

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <LocaleProvider>
      <App />
    </LocaleProvider>
  </React.StrictMode>,
)
